import {
	ContextMenu,
	ContextMenuDeleteWithModelUsageItem,
	ContextMenuItem,
	Id,
	isSuccess,
	useHasPermission,
} from "@dgs/core";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useCreateOnDemandFilter } from "../../../shared/filters";
import { FilterMode } from "../../../shared/filters/filterTypes";
import { PermissionName } from "../../../shared/types/permission";
import { locationService } from "../shared/api/locationService";

interface Props {
	locationId: Id;
	handleEdit: () => void;
	handleDelete: () => Promise<any>;
}

export const LocationListContextMenu: FC<Props> = ({ locationId, handleEdit, handleDelete }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { createOnDemandFilter } = useCreateOnDemandFilter();

	const showUsageAction = useCallback(() => locationService.usages(locationId).then((r) => r.data.data), [locationId]);

	const showEvents = useCallback(async () => {
		const result = await createOnDemandFilter("EVENT", {
			mode: FilterMode.ADVANCED,
			filterItems: [
				{
					field: "LOCATION",
					operator: "EQUAL_RELATION",
					value: locationId,
					entity: "EVENT",
					operators: [],
				},
			],
		});

		if (isSuccess(result)) {
			navigate(`/events/active?filterId=${result.data.data.id}`);
		}
	}, [createOnDemandFilter, locationId, navigate]);

	const canUpdate = useHasPermission<PermissionName>(["location.update"]);
	const canDelete = useHasPermission<PermissionName>(["location.delete"]);
	const canReadEvents = useHasPermission<PermissionName>(["event.read"]);

	return (
		<>
			<ContextMenu displayShortcutsAs="icon">
				{canUpdate && (
					<ContextMenuItem label={t("Edit")} action={handleEdit} title={t("Edit")} icon="edit" shortcut="primary" />
				)}
				{canDelete && (
					<ContextMenuDeleteWithModelUsageItem
						label={t("Delete")}
						title={t("Delete")}
						showUsageAction={showUsageAction}
						modelName={t("Location")}
						confirmationBody={t("Are you sure you want to delete this location?")}
						deleteAction={handleDelete}
						shortcut="danger"
					/>
				)}
				{canReadEvents && (
					<ContextMenuItem label={t("Show events")} action={showEvents} title={t("Show events")} icon="eye" />
				)}
			</ContextMenu>
		</>
	);
};
